import { localTime } from '@beelday/common'
import { createAction, createSlice } from '@reduxjs/toolkit'
import { RootState } from 'common/redux'
import { QuizId } from 'common/types'
import { parseISO } from 'date-fns'
import { first, last, map } from 'lodash'
import { Activity, ScriptItem, SessionChunk } from './script-model'
import { createSelector } from 'reselect'

type QuizActivityDto = {
	type: 'QUIZ'
	state: {
		onBeginData?: {
			quizId: QuizId
		}
	}
	needsConfirmation: boolean
}

type BreakActivityDto = { type: 'BREAK'; needsConfirmation: boolean }

type AudioStreamingActivityDto = {
	type: 'AUDIO_STREAMING'
	needsConfirmation: boolean
	state: {
		configuration: {
			id: string
			name: string
			description: string
			creatorId: string
			fileName: string
			originalFilename: string
		}
	}
}

type YouTubeStreamingActivityDto = {
	type: 'YOUTUBE'
	needsConfirmation: boolean
	state: {
		configuration: {
			id: string
			name: string
			description: string
			creatorId: string
			link: string
		}
	}
}

type SimpleGroupingActivityDto = {
	type: 'SIMPLE_GROUPING'
	needsConfirmation: boolean
	state: {
		configuration: {
			name?: string
			description?: string
		}
	}
}

type ActivityDto =
	| QuizActivityDto
	| BreakActivityDto
	| AudioStreamingActivityDto
	| YouTubeStreamingActivityDto
	| SimpleGroupingActivityDto

type ScriptItemDto = {
	title: string
	description: string
	startTime: string
	activities: ActivityDto[]
}

type SessionChunkDto = {
	startDate: string
	startTime: string
	startTitle?: string
	startDescription?: string
	endTime: string
	endTitle?: string
	endDescription?: string
	items: ScriptItemDto[]
}

type ScriptStateChangedSSE = {
	placeId: string
	currentItemIndex: number
	currentActivityIndex: number
	currentSessionChunkIndex: number
	sessionChunks: SessionChunkDto[]
}

type ScriptState = {
	currentItemIndex: number
	currentActivityIndex: number
	currentSessionChunkIndex: number
	chunks: SessionChunk[]
}

const initialState: ScriptState = {
	currentItemIndex: 0,
	currentActivityIndex: 0,
	currentSessionChunkIndex: 0,
	chunks: [],
}

const scriptChanged = createAction<ScriptStateChangedSSE>(
	'SSE_SCRIPT_STATE_CHANGED'
)

const dtoToChunk = (dto: SessionChunkDto): SessionChunk => {
	return {
		...dto,
		startDate: parseISO(dto.startDate),
		startTime: localTime.parse(dto.startTime),
		//FIXME: this should be passed from backend
		endTime: localTime.parse(dto.endTime || '23:59'),
		items: map(dto.items, dtoToItem),
	}
}

const dtoToItem = (dto: ScriptItemDto): ScriptItem => {
	const activities = map(dto.activities, a => {
		if (a.type === 'QUIZ') {
			return {
				type: a.type,
				quizId: a.state.onBeginData?.quizId,
				needsConfirmation: a?.needsConfirmation,
			}
		}
		return a
	})

	return {
		...dto,
		activities,
		startTime: parseISO(dto.startTime),
	}
}

export const scriptSlice = createSlice({
	name: 'script',
	initialState,
	reducers: {},
	extraReducers: builder => {
		builder.addCase(scriptChanged, (state, { payload }) => {
			state.currentItemIndex = payload.currentItemIndex
			state.currentActivityIndex = payload.currentActivityIndex
			state.currentSessionChunkIndex = payload.currentSessionChunkIndex
			state.chunks = map(payload.sessionChunks, dtoToChunk)
		})
	},
})

type ScriptStep = {
	chunk: SessionChunk
	item: ScriptItem
	activity?: Activity
}

type CurrentScriptStep = ScriptStep & { chunkIndex: number }

const selectScript = (state: RootState): ScriptState => state.script

const selectCurrentScriptStep = (
	state: RootState
): CurrentScriptStep | undefined => {
	const {
		currentItemIndex,
		currentActivityIndex,
		currentSessionChunkIndex,
		chunks,
	} = state.script

	const chunk = chunks[currentSessionChunkIndex]
	const item = chunk?.items[currentItemIndex]
	if (item) {
		const activity = item.activities[currentActivityIndex]

		return {
			chunk,
			item,
			activity,
			chunkIndex: currentSessionChunkIndex,
		}
	}
}

/**
 * The server's script state has arrived. `chunks` is `[]` until the first
 * `SSE_SCRIPT_STATE_CHANGED` is applied - the room's INITIAL_STATE replay or a
 * live one - and that one action writes the chunks and every index together.
 * So the indices are the server's whenever this is true, never the zeros the
 * slice starts with; on a reload that is the moment a running agenda stops
 * looking like an unstarted one.
 */
const selectScriptLoaded = (state: RootState): boolean =>
	state.script.chunks.length > 0

/**
 * The server has moved the script off "not yet started", which it reports as
 * item index -1. Read from the index itself rather than from `current`, so a
 * state that merely fails to resolve to an item is not taken for unstarted.
 * Before the state has loaded the slice's 0 reads as started: the safe side.
 */
const selectScriptStarted = (state: RootState): boolean =>
	state.script.currentItemIndex >= 0

/** The chunk the script starts in has items: what the Start script bar needs. */
const selectScriptHasItems = (state: RootState): boolean =>
	(state.script.chunks[state.script.currentSessionChunkIndex]?.items.length ??
		0) > 0

const selectScriptStep =
	(direction: -1 | 1) =>
	({
		currentItemIndex,
		currentActivityIndex,
		currentSessionChunkIndex,
		chunks,
	}: ScriptState): ScriptStep | undefined => {
		const rolloverSelector = direction > 0 ? first : last

		let chunk = chunks[currentSessionChunkIndex]
		let item: ScriptItem | undefined = chunk?.items[currentItemIndex]
		let activity: Activity | undefined =
			item?.activities[currentActivityIndex + direction]

		if (!activity) {
			item = chunk?.items[currentItemIndex + direction]
			activity = rolloverSelector(item?.activities)
		}

		if (!item) {
			chunk = chunks?.[currentSessionChunkIndex + direction]
			item = rolloverSelector(chunk?.items)
			activity = rolloverSelector(item?.activities)
		}

		if (item) {
			return {
				chunk,
				item,
				activity,
			}
		} else {
			return undefined
		}
	}

const selectNextScriptStep = createSelector(
	(state: RootState) => state.script,
	selectScriptStep(1)
)
const selectPrevScriptStep = createSelector(
	(state: RootState) => state.script,
	selectScriptStep(-1)
)

//Selectors
export {
	selectScript,
	selectScriptLoaded,
	selectScriptStarted,
	selectScriptHasItems,
	selectCurrentScriptStep,
	selectPrevScriptStep,
	selectNextScriptStep,
}

//Reducer
export const reducer = scriptSlice.reducer
